import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { API_BASE, getPlatformAssetUrl, getSchoolAssetUrl } from '../utils/api';
import { getTokenSync } from '../utils/authStorage';
import { getCpRawatLogoUrl } from '../utils/studentCardUrl';
import { isMobileContainer, isNativePlatform } from '../utils/device';
import { downloadBlob } from '../utils/blobDownload';
import {
  fetchFeeReceiptPdfBlob,
  getFeeReceiptPdfUrl,
} from '../utils/documentPortalUrl';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import PdfEmbed from './PdfEmbed';
import { ArrowLeft, Download, FileText, Loader2 } from 'lucide-react';

const centerScreenStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fff',
};

const spinnerStyle = {
  border: '3px solid #e5e7eb',
  borderTopColor: '#3b82f6',
  borderRadius: '50%',
  width: 40,
  height: 40,
  animation: 'spin 0.8s linear infinite',
};

const publicApi = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getPdfViewerUrl(token) {
  return `${getFeeReceiptPdfUrl(token)}#toolbar=1&navpanes=0&zoom=100`;
}

async function fetchPortalMeta(token) {
  try {
    const res = await publicApi.get('public/invoice-portal', { params: { token } });
    if (res.data?.data?.document) return res.data.data;
  } catch (_) {
    // try auth fallback
  }
  if (getTokenSync()) {
    const res = await api.get('fees/portal', { params: { token } });
    if (res.data?.data?.document) return res.data.data;
  }
  return null;
}

function getDocumentTitle(meta) {
  const doc = meta?.document || {};
  if (meta?.type === 'fee_receipt') {
    return doc.receipt_no ? `Receipt ${doc.receipt_no}` : 'Fee receipt';
  }
  if (doc.month && doc.year) {
    return `Salary slip ${MONTH_NAMES[Number(doc.month)] || doc.month} ${doc.year}`;
  }
  return 'Salary slip';
}

function getDownloadName(meta) {
  const doc = meta?.document || {};
  if (meta?.type === 'fee_receipt' && doc.receipt_no) {
    return `receipt-${String(doc.receipt_no).replace(/[^a-zA-Z0-9_-]/g, '')}.pdf`;
  }
  return 'document.pdf';
}

function getBackPath(roleSlug) {
  if (roleSlug === 'parent') return '/portal/parent/fees';
  if (roleSlug === 'student') return '/portal/student/fees';
  return null;
}

export function PublicInvoicePortal({ token: tokenProp }) {
  const token = tokenProp || null;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const viewerRef = useRef(null);
  const { toast } = useToast();
  const isNativeMobile = isMobileContainer() && isNativePlatform();
  const pdfUrl = token ? getPdfViewerUrl(token) : '';

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing link');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const metaData = await fetchPortalMeta(token);
        if (cancelled) return;

        if (!metaData) {
          setError('Document not found');
          setMeta(null);
          return;
        }

        setMeta(metaData);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Document not found');
          setMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !meta || !isNativeMobile) {
      setPdfBlob(null);
      setPdfError(null);
      setPdfLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setPdfLoading(true);
      setPdfError(null);
      setPdfBlob(null);
      try {
        const blob = await fetchFeeReceiptPdfBlob(token);
        if (cancelled) return;
        setPdfBlob(blob);
      } catch (err) {
        if (!cancelled) {
          setPdfError(err.message || 'Could not load PDF preview');
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, meta, isNativeMobile]);

  const handleBack = useCallback(() => {
    const backPath = getBackPath(user?.role_slug);
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (backPath) {
      navigate(backPath);
      return;
    }
    navigate('/login');
  }, [navigate, user?.role_slug]);

  const handleViewReceipt = useCallback(() => {
    viewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleDownload = useCallback(async () => {
    if (!token || downloading) return;
    const filename = getDownloadName(meta);
    setDownloading(true);
    try {
      if (isNativeMobile && pdfBlob) {
        const result = await downloadBlob(pdfBlob, filename);
        if (result?.saved) {
          toast({
            title: 'Saved to Downloads',
            description: result.filename || filename,
          });
        }
        return;
      }
      const a = document.createElement('a');
      a.href = getFeeReceiptPdfUrl(token);
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not save PDF',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  }, [token, meta, isNativeMobile, pdfBlob, downloading, toast]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" style={centerScreenStyle}>
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" style={spinnerStyle} />
      </div>
    );
  }

  if (error || !meta || !pdfUrl) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center bg-white p-6 text-center"
        style={{ ...centerScreenStyle, flexDirection: 'column', padding: 24 }}
      >
        <FileText className="mb-3 h-10 w-10 text-gray-400" />
        <p className="mb-4 text-gray-500" style={{ color: '#6b7280', marginBottom: 16 }}>
          {error || 'Document not found'}
        </p>
        <Link to="/login" className="text-sm text-blue-600 hover:underline" style={{ color: '#2563eb', fontSize: 14 }}>
          Go to login
        </Link>
      </div>
    );
  }

  const school = meta.document?.school || {};
  const schoolLogo = getSchoolAssetUrl(school.logo, school.id);
  const platformLogo = getPlatformAssetUrl(meta.platform_branding?.logo);
  const managedLogo = platformLogo || getCpRawatLogoUrl();
  const title = getDocumentTitle(meta);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="border-b border-gray-200 bg-white px-4 py-3 pt-safe">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {isNativeMobile ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                onClick={handleBack}
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : null}
            {schoolLogo ? (
              <img
                src={schoolLogo}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full border border-gray-200 bg-white object-contain p-0.5"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-bold text-white">
                {(school.name || 'S').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">{school.name || 'School'}</h1>
              <p className="text-xs text-gray-500">{title}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isNativeMobile ? (
              <Button
                type="button"
                size="sm"
                className="bg-gray-900 text-white hover:bg-gray-800"
                onClick={handleViewReceipt}
              >
                <FileText className="mr-1.5 h-4 w-4" />
                View receipt
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleDownload}
              disabled={downloading || (isNativeMobile && (pdfLoading || !pdfBlob))}
            >
              {downloading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" />
              )}
              Download
            </Button>
          </div>
        </div>
      </header>

      <main ref={viewerRef} className="flex flex-1 flex-col bg-neutral-700">
        {isNativeMobile && pdfLoading ? (
          <div className="flex min-h-[calc(100vh-7.5rem)] flex-1 flex-col items-center justify-center gap-3 bg-neutral-100 px-6 py-12 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-gray-400" />
            <p className="text-sm text-gray-600">Loading receipt preview…</p>
          </div>
        ) : isNativeMobile && pdfError ? (
          <div className="flex min-h-[calc(100vh-7.5rem)] flex-1 flex-col items-center justify-center gap-4 bg-neutral-100 px-6 py-12 text-center">
            <FileText className="h-12 w-12 text-gray-400" />
            <p className="max-w-sm text-sm text-red-600">{pdfError}</p>
            <Button type="button" variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Go back
            </Button>
          </div>
        ) : (
          <PdfEmbed
            title={title}
            blob={isNativeMobile ? pdfBlob : null}
            url={isNativeMobile ? '' : pdfUrl}
            className="min-h-[calc(100vh-7.5rem)] w-full flex-1 border-0 bg-neutral-700"
          />
        )}
      </main>

      <footer className="border-t border-gray-100 bg-white py-3 text-center pb-safe">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
          <span>Managed by</span>
          <img
            src={managedLogo}
            alt="CPRawat Softwares"
            className="h-12 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </footer>
    </div>
  );
}

export default PublicInvoicePortal;
